import { pushNotifications } from '../api/client'

/**
 * Web Push subscription manager.
 * Handles the full lifecycle: service worker registration,
 * PushManager subscription, and server-side persistence.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

function getSwUrl() {
  const path = window.location.pathname
  const match = path.match(/^(.*\/WebObjects\/forge\.woa)/)
  return match ? `${match[1]}/wa/portalServiceWorker` : '/wa/portalServiceWorker'
}

/**
 * Register the service worker, request permission, subscribe to push,
 * and send the subscription to the server.
 * @returns {PushSubscription|null} The subscription, or null if not possible.
 */
export async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn('Push notifications not supported in this browser')
    return null
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    console.warn('Notification permission denied:', permission)
    return null
  }

  // Register the service worker
  const reg = await navigator.serviceWorker.register(getSwUrl(), { scope: '/' })
  await navigator.serviceWorker.ready

  // Get the VAPID public key from the server
  const { publicKey, configured } = await pushNotifications.getVapidKey()
  if (!configured || !publicKey) {
    console.warn('Web push not configured on server')
    return null
  }

  // Check for existing subscription and unsubscribe if keys differ
  const existing = await reg.pushManager.getSubscription()
  if (existing) {
    // Already subscribed -- re-send to server in case it was lost
    await pushNotifications.subscribe(existing)
    return existing
  }

  // Create new subscription
  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  })

  // Persist on the server
  await pushNotifications.subscribe(subscription)
  return subscription
}

/**
 * Unsubscribe from push notifications (both locally and on the server).
 */
export async function unsubscribeFromPush() {
  if (!('serviceWorker' in navigator)) return
  const reg = await navigator.serviceWorker.getRegistration()
  if (!reg) return
  const subscription = await reg.pushManager.getSubscription()
  if (subscription) {
    try {
      await pushNotifications.unsubscribe(subscription.endpoint)
    } catch (e) {
      console.warn('Failed to delete server subscription:', e)
    }
    await subscription.unsubscribe()
  }
}

/**
 * Check whether the browser currently has an active push subscription.
 * @returns {boolean}
 */
export async function isPushSubscribed() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false
  try {
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) return false
    const sub = await reg.pushManager.getSubscription()
    return !!sub
  } catch {
    return false
  }
}
